import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import api, { mediaUrl, formatPrice } from "@/lib/api";
import { toast } from "sonner";
import ProductCard from "@/components/storefront/ProductCard";

const AuthForms = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { mode === "login" ? await login(f.email, f.password) : await register(f.email, f.password, f.name); }
    catch (er) { const d = er.response?.data?.detail; setErr(typeof d === "string" ? d : "Something went wrong."); }
    setBusy(false);
  };
  return (
    <div className="max-w-sm mx-auto px-6 pt-20 pb-32">
      <p className="overline text-gold text-center mb-3">Your NoirbyAmara</p>
      <h1 className="font-serif text-5xl leading-none text-center mb-10">{mode === "login" ? "Welcome back." : "Join us."}</h1>
      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" data-testid="account-name"
            className="w-full bg-cream border border-espresso/20 px-4 py-3.5 text-sm outline-none focus:border-espresso" />
        )}
        <input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" data-testid="account-email"
          className="w-full bg-cream border border-espresso/20 px-4 py-3.5 text-sm outline-none focus:border-espresso" />
        <input type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password" data-testid="account-password"
          className="w-full bg-cream border border-espresso/20 px-4 py-3.5 text-sm outline-none focus:border-espresso" />
        {err && <p className="text-sm text-wine">{err}</p>}
        <button type="submit" disabled={busy} data-testid="account-submit"
          className="w-full bg-espresso text-cream py-3.5 text-[0.72rem] tracking-[0.2em] uppercase hover:bg-cocoa transition-colors disabled:opacity-50">
          {busy ? "…" : mode === "login" ? "Sign In" : "Create Account"}
        </button>
      </form>
      <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} className="w-full text-center text-xs text-espresso/60 mt-5 hover-underline mx-auto">
        {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
      </button>
    </div>
  );
};

const ADMIN_ROLES = ["owner", "store_manager", "content", "client_care"];

const AccountPage = () => {
  const { user, logout } = useAuth();
  const { saved } = useStore();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [savedProducts, setSavedProducts] = useState([]);
  const [name, setName] = useState("");
  const [addr, setAddr] = useState({ line1: "", city: "", postcode: "", country: "" });

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    api.get("/account/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/account/addresses").then((r) => { if (r.data[0]) setAddr(r.data[0]); }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (tab === "saved" && saved.length) {
      Promise.all(saved.map((s) => api.get(`/products/${s}`).then((r) => r.data.product).catch(() => null))).then((r) => setSavedProducts(r.filter(Boolean)));
    }
  }, [tab, saved]);

  if (user === null) return <div className="min-h-[60vh]" />;
  if (!user) return <AuthForms />;

  const saveProfile = async () => { await api.put("/account/profile", { name }); toast.success("Profile updated"); };
  const saveAddr = async () => { await api.put("/account/addresses", { addresses: [addr] }); toast.success("Address saved"); };

  const TABS = [["orders", "Orders"], ["saved", "Saved Pieces"], ["addresses", "Addresses"], ["profile", "Profile"]];

  return (
    <div className="max-w-4xl mx-auto px-6 pt-16 md:pt-24 pb-24 min-h-[60vh]">
      <p className="overline text-gold mb-3">Your NoirbyAmara</p>
      <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
        <h1 className="font-serif text-5xl md:text-6xl leading-none">Hello{user.name ? `, ${user.name}` : ""}.</h1>
        <button onClick={logout} className="text-[0.7rem] tracking-[0.18em] uppercase hover-underline">Sign Out</button>
      </div>
      {ADMIN_ROLES.includes(user.role) && (
        <Link to="/admin" data-testid="account-admin-link"
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-cocoa text-cream px-6 py-4 mb-8 hover:bg-espresso transition-colors">
          <span>
            <span className="overline text-gold block mb-1">Atelier access</span>
            <span className="font-serif text-xl">This is your customer view — enter the Atelier CMS to manage your store</span>
          </span>
          <span className="text-[0.72rem] tracking-[0.2em] uppercase shrink-0">Enter Admin →</span>
        </Link>
      )}
      <div className="flex gap-6 border-b border-espresso/15 mb-8 text-[0.72rem] tracking-[0.14em] uppercase overflow-x-auto">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`account-tab-${k}`}
            className={`pb-3 whitespace-nowrap border-b-2 -mb-px ${tab === k ? "border-espresso" : "border-transparent text-espresso/50"}`}>{l}</button>
        ))}
      </div>

      {tab === "orders" && (
        orders.length ? (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.id} data-testid={`account-order-${o.order_number}`} className="bg-sand p-5">
                <div className="flex justify-between mb-3 pb-3 border-b border-espresso/10 text-sm">
                  <span className="font-medium">{o.order_number}</span>
                  <span className="overline text-espresso/60">{o.fulfilment_status} · {formatPrice(o.total)}</span>
                </div>
                {o.items.map((i, x) => <p key={x} className="text-sm text-espresso/70">{i.name} · {i.colour}/{i.size} × {i.qty}</p>)}
                {o.tracking && <p className="text-xs text-espresso/50 mt-2">Tracking: {o.tracking}</p>}
              </div>
            ))}
          </div>
        ) : <p className="font-serif text-3xl text-espresso/50 py-16">No orders yet.</p>
      )}

      {tab === "saved" && (
        savedProducts.length ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">{savedProducts.map((p) => <ProductCard key={p.id} product={p} />)}</div>
        ) : <p className="font-serif text-3xl text-espresso/50 py-16">Nothing saved. Yet.</p>
      )}

      {tab === "addresses" && (
        <div className="max-w-md space-y-4">
          {["line1", "city", "postcode", "country"].map((k) => (
            <input key={k} data-testid={`account-address-${k}`} value={addr[k] || ""} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} placeholder={k === "line1" ? "Address" : k}
              className="w-full bg-cream border border-espresso/20 px-4 py-3 text-sm outline-none focus:border-espresso capitalize" />
          ))}
          <button onClick={saveAddr} data-testid="account-address-save" className="bg-espresso text-cream px-8 py-3 text-[0.7rem] tracking-[0.18em] uppercase">Save Address</button>
        </div>
      )}

      {tab === "profile" && (
        <div className="max-w-md space-y-4">
          <input value={name} data-testid="account-profile-name" onChange={(e) => setName(e.target.value)} placeholder="Name"
            className="w-full bg-cream border border-espresso/20 px-4 py-3 text-sm outline-none focus:border-espresso" />
          <input value={user.email} disabled className="w-full bg-sand/50 border border-espresso/20 px-4 py-3 text-sm text-espresso/50" />
          <button onClick={saveProfile} data-testid="account-profile-save" className="bg-espresso text-cream px-8 py-3 text-[0.7rem] tracking-[0.18em] uppercase">Save Profile</button>
        </div>
      )}
    </div>
  );
};

export default AccountPage;
